import React, { useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tip } from '../../../../components/Tip'
import { useTranslation } from 'react-i18next'

// Helper function to format time
const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

interface NavigationHeaderProps {
    onToggleExpand: () => void
    currentSessionTitle?: string
    stepTitle?: string
    stepNumber?: number
    totalSteps?: number
    timeRemaining: number | null
    isTimerExpired: boolean
    onPrevStep: () => void
    onNextStep: () => void
    isPrevDisabled: boolean
    isNextDisabled: boolean
}

const NavigationHeader: React.FC<NavigationHeaderProps> = ({
    currentSessionTitle,
    stepTitle,
    stepNumber,
    totalSteps,
    timeRemaining,
    isTimerExpired,
    onPrevStep,
    onNextStep,
    isPrevDisabled,
    isNextDisabled,
}) => {
    const { t } = useTranslation('sidebar')
    const [isExpanded, setIsExpanded] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    return (
        <Navigation 
            onClick={() => setIsExpanded(!isExpanded)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isExpanded ? (
                <ExpandedContent>
                    <HeaderSection>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                            <StepNumberLabel>
                                {t('content.stepNumber')}
                                {stepNumber !== undefined && totalSteps !== undefined && (
                                    <span style={{ marginLeft: '5px' }}>
                                        {stepNumber}
                                        <span style={{ margin: '0 2px' }}>/</span>
                                        {totalSteps}
                                    </span>
                                )}
                            </StepNumberLabel>
                            {timeRemaining !== null && (
                                <CountdownTimer isExpired={isTimerExpired}>
                                    <FontAwesomeIcon
                                        icon={['far', 'clock']}
                                        style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                                    />
                                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                        {formatTime(Math.abs(timeRemaining))}
                                    </span>
                                </CountdownTimer>
                            )}
                        </div>
                        <NavButtonGroup onClick={e => e.stopPropagation()}>
                            <NavButton
                                onClick={onPrevStep}
                                disabled={isPrevDisabled}
                                data-tooltip-id="prev-step-tooltip"
                            >
                                <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                            </NavButton>
                            <Tip id="prev-step-tooltip" content={t('content.previousStep', 'Previous step')} />

                            <NavButton
                                onClick={onNextStep}
                                disabled={isNextDisabled}
                                data-tooltip-id="next-step-tooltip"
                            >
                                <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                            </NavButton>
                            <Tip id="next-step-tooltip" content={t('content.nextStep', 'Next step')} />
                        </NavButtonGroup>
                    </HeaderSection>
                    <StepTitle isExpanded={true}>{stepTitle || ''}</StepTitle>
                    {currentSessionTitle && <SessionTitle>{currentSessionTitle}</SessionTitle>}
                    <HoverChevron isVisible={isHovered}>
                        <FontAwesomeIcon icon={['far', 'circle-chevron-up']} />
                    </HoverChevron>
                </ExpandedContent>
            ) : (
                <CollapsedContent>
                    <StepTitleContainer isExpanded={isTimerExpired}>
                        <StepTitle isExpanded={false}>{stepTitle || ''}</StepTitle>
                        {timeRemaining !== null && (
                            <CountdownTimer isExpired={isTimerExpired}>
                                <FontAwesomeIcon
                                    icon={['far', 'clock']}
                                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                                />
                                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                    {formatTime(Math.abs(timeRemaining))}
                                </span>
                            </CountdownTimer>
                        )}
                    </StepTitleContainer>

                    <NavButtonGroup onClick={e => e.stopPropagation()}>
                        <NavButton
                            onClick={onPrevStep}
                            disabled={isPrevDisabled}
                            data-tooltip-id="prev-step-tooltip-collapsed"
                        >
                            <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                        </NavButton>
                        <Tip id="prev-step-tooltip-collapsed" content={t('content.previousStep', 'Previous step')} />

                        <NavButton
                            onClick={onNextStep}
                            disabled={isNextDisabled}
                            data-tooltip-id="next-step-tooltip-collapsed"
                        >
                            <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                        </NavButton>
                        <Tip id="next-step-tooltip-collapsed" content={t('content.nextStep', 'Next step')} />
                    </NavButtonGroup>
                    <HoverChevron isVisible={isHovered}>
                        <FontAwesomeIcon icon={['far', 'circle-chevron-down']} />
                    </HoverChevron>
                </CollapsedContent>
            )}
        </Navigation>
    )
}

export default NavigationHeader

const Navigation = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;

    &:hover {
        background-color: rgba(255, 255, 255, 0.05);
    }
        border-radius:8px;
`

const CollapsedContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const ExpandedContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
`

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const StepNumberLabel = styled.div`
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
`

const SessionTitle = styled.div`
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    width: 100%;
    margin-top: 0.25rem;
`

const StepTitleContainer = styled.div<{ isExpanded: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: ${props => (props.isExpanded ? 'normal' : 'nowrap')};
    width: 100%;
`

const StepTitle = styled.h3<{ isExpanded: boolean }>`
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: white;
    text-align: left;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: ${props => (props.isExpanded ? 'normal' : 'nowrap')};
`

const CountdownTimer = styled.div<{ isExpired: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: ${props => (props.isExpired ? '#ff5555' : '#ffffff')};

    svg {
        font-size: 0.8rem;
    }
`

const NavButtonGroup = styled.div`
    display: flex;
    gap: 0.5rem;
`

const NavButton = styled.button`
    background: none;
    border: none;
    color: white;
    padding: 0.5rem;
    cursor: pointer;
    opacity: ${props => (props.disabled ? 0.5 : 1)};
    pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
        color: ${props => props.theme.color.electricBlue};
        background-color: rgba(255, 255, 255, 0.1);
    }
`

const HoverChevron = styled.div<{ isVisible: boolean }>`
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.5);
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
    
    svg {
        font-size: 1rem;
    }
`
